const DATA_REVIEWER = "DATA_REVIEWER";
const PRICING = "PRICING";

const ROUTE_RESOURCE_MAP = [
  { matcher: "/", resource: "dashboard" },
  { matcher: "/dashboard", resource: "dashboard" },
  { matcher: "/assets-valuation", resource: "valuations" },
  { matcher: "/data-review", resource: "valuations" },
  { matcher: "/deleted-assets", resource: "valuations" },
  { matcher: "/assets-by-category", resource: "valuations" },
  { matcher: "/pahs", resource: "valuations" },
  { matcher: "/mortgages-valuation", resource: "valuations" },
  { matcher: "/asset-pricing", resource: "pricing" },
  { matcher: "/asset-pricing-formulas", resource: "pricing" },
  { matcher: "/report-summary-management", resource: "pricing" },
  { matcher: "/api-error", resource: "system" },
];

const ROLE_POLICIES = {
  [DATA_REVIEWER]: {
    dashboard: ["view"],
    valuations: ["view"],
    system: ["view"],
  },
  [PRICING]: {
    dashboard: ["view"],
    pricing: ["view"],
    system: ["view"],
  },
};

const normalizePath = (value = "") => {
  const [pathname = ""] = String(value).split("?");
  if (!pathname || pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
};

export const getStoredAuthUser = () => {
  const authUser = localStorage.getItem("authUser");
  if (!authUser) return null;

  try {
    return JSON.parse(authUser);
  } catch (error) {
    console.error("Error parsing authUser:", error);
    return null;
  }
};

export const getCurrentUser = () => getStoredAuthUser()?.user || null;

export const getCurrentUserRoleCodes = () => {
  const user = getCurrentUser();
  if (!user) return [];

  const candidates = [
    ...(Array.isArray(user.roles) ? user.roles : []),
    ...(Array.isArray(user.user_roles) ? user.user_roles : []),
    ...(user.role ? [user.role] : []),
    ...(user.primary_role ? [user.primary_role] : []),
  ];

  const codes = candidates
    .map((role) => {
      if (!role) return null;
      if (typeof role === "string") return role;
      return role.code || role.role_code || role.roleCode || role.name || null;
    })
    .filter(Boolean)
    .map((value) => String(value).trim().toUpperCase());

  return [...new Set(codes)];
};

export const hasRoleCode = (roleCode) =>
  getCurrentUserRoleCodes().includes(String(roleCode || "").trim().toUpperCase());

const getActiveRolePolicies = () =>
  getCurrentUserRoleCodes()
    .map((roleCode) => ROLE_POLICIES[roleCode])
    .filter(Boolean);

const getResourceForRoute = (pathname) => {
  const normalizedPath = normalizePath(pathname);
  const matchedRoute = ROUTE_RESOURCE_MAP.find(({ matcher }) => matcher === normalizedPath);
  return matchedRoute?.resource || null;
};

const hasPermissionForAction = (resource, action) => {
  const roleCodes = getCurrentUserRoleCodes();
  if (!roleCodes.length) return true;

  const activePolicies = getActiveRolePolicies();
  if (!activePolicies.length) return true;

  for (const roleCode of roleCodes) {
    const policy = ROLE_POLICIES[roleCode];
    if (!policy) {
      continue;
    }

    const allowedActions = policy[resource];
    if (allowedActions?.includes(action)) {
      return true;
    }
  }

  return false;
};

export const canView = (resourceOrPath) => {
  const resource = String(resourceOrPath || "").startsWith("/")
    ? getResourceForRoute(resourceOrPath)
    : resourceOrPath;

  if (!resource) {
    return getActiveRolePolicies().length === 0;
  }
  return hasPermissionForAction(resource, "view");
};

export const canCreate = (resource) => hasPermissionForAction(resource, "create");
export const canEdit = (resource) => hasPermissionForAction(resource, "edit");
export const canDelete = (resource) => hasPermissionForAction(resource, "delete");

export const canAccessPath = (pathname) => canView(pathname);

const filterSubMenuItems = (items = []) =>
  items
    .map((item) => {
      if (item.link) {
        return canAccessPath(item.link) ? item : null;
      }

      if (item.subMenu) {
        const filteredSubMenu = item.subMenu.filter((subItem) => canAccessPath(subItem.link));
        return filteredSubMenu.length ? { ...item, subMenu: filteredSubMenu } : null;
      }

      return item;
    })
    .filter(Boolean);

export const filterSidebarItemsByAccess = (items = []) =>
  items
    .map((item) => {
      if (item.isMainMenu) return item;
      if (item.url) return canAccessPath(item.url) ? item : null;
      if (item.subItem) {
        const filteredSubItems = filterSubMenuItems(item.subItem);
        return filteredSubItems.length ? { ...item, subItem: filteredSubItems } : null;
      }
      return item;
    })
    .filter(Boolean);
